import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Lock } from "@phosphor-icons/react/Lock";
import { paths } from "config/paths";
import Link from "next/link";
// import { useRef, useState } from "react";
import { PropertyItem } from "~ui-components/components/atoms/property-item";
import { PropertyList } from "~ui-components/components/atoms/property-list";
import { SUBSCRIPTION_CYCLE } from "~ui-components/types/__generated/gql/graphql";
import { useCheckoutPricing } from "~utils/recurly/use-checkout-pricing";
import { PLAN_NAME } from "../../user-accounts";

const BILLING_CYCLE_DESCRIPTION: Record<SUBSCRIPTION_CYCLE, string> = {
	MONTHLY: "Per month",
	ANNUAL: "Per year",
	TWO_YEARS: "Per 2 years",
	THREE_YEARS: "Per 3 years",
	FOUR_YEARS: "Per 4 years",
	FIVE_YEARS: "Per 5 years",
};

export const TrialCheckoutSubscriptionSummary = ({
	form,
	subscriptionConfig,
}) => {
	const { pricing, pricingError } = useCheckoutPricing(subscriptionConfig, {
		first_name: form.values.firstName,
		last_name: form.values.lastName,
		address1: form.values.streetLine1,
		city: form.values.city,
		postal_code: form.values.postalCode,
		country: form.values.country,
		address2: form.values.streetLine2,
		vat_number: form.values.vatNumber,
		state: form.values.province,
	});

	return (
		<Stack spacing={4}>
			<Card>
				<CardHeader
					title={`Subscribe to ${PLAN_NAME[subscriptionConfig.plan]}`}
					subheader={
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							color="text.primary"
							mt={2}>
							<Typography variant="h4">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.total ?? "0.00"}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary">
								{BILLING_CYCLE_DESCRIPTION[subscriptionConfig.cycle]}
							</Typography>
						</Stack>
					}
				/>
				<PropertyList
					divider={<Divider />}
					sx={{
						"--PropertyItem-padding": "12px 24px",
						"--PropertyItem-Value-Align": "right",
					}}>
					<PropertyItem
						name={
							<Typography
								color="text.primary"
								variant="body2">
								{PLAN_NAME[subscriptionConfig.plan]}
							</Typography>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="body2">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.items?.[0]?.plan ?? "0.00"}
							</Typography>
						}
					/>
					<PropertyItem
						name={
							<Stack spacing={1}>
								<Typography
									color="text.primary"
									variant="body2">
									Addons
								</Typography>
								<Typography
									color="text.secondary"
									variant="caption">
									{Object.keys(subscriptionConfig.addons)
										.filter((key) => subscriptionConfig.addons[key])
										.map((key) => `${subscriptionConfig.addons[key]} ${key}`)
										.join(", ")}
								</Typography>
							</Stack>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="body2">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.items?.[0]?.addons ?? "0.00"}
							</Typography>
						}
					/>
					<PropertyItem
						name={
							<Typography
								color="text.primary"
								variant="subtitle2">
								Subtotal
							</Typography>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="subtitle2">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.subtotal ?? "0.00"}
							</Typography>
						}
					/>
					{/* <PropertyItem
						name={
							<Stack spacing={1}>
								<Stack
									direction={"row"}
									spacing={1}
									alignItems={"center"}>
									<Typography
										color="text.primary"
										variant="body2">
										Discount
									</Typography>
									<Tag />
								</Stack>
								<CouponInput />
							</Stack>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="body2">
								- $ {pricing.price?.now?.discount}
							</Typography>
						}
					/> */}
					<PropertyItem
						name={
							<Stack spacing={1}>
								<Typography
									color="text.primary"
									variant="body2">
									Taxes
								</Typography>
								{!!pricing.price?.taxes?.length &&
									pricing.price.taxes.map((tax) => (
										<Typography
											key={tax.region}
											color="text.secondary"
											variant="caption">
											{tax.region} VAT {+(tax.rate ?? 0) * 100}%
										</Typography>
									))}
							</Stack>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="body2">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.taxes ?? "0.00"}
							</Typography>
						}
					/>
					<PropertyItem
						name={
							<Typography
								color="text.primary"
								variant="subtitle2">
								Total
							</Typography>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="subtitle2">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.total ?? "0.00"}
							</Typography>
						}
					/>
				</PropertyList>
			</Card>
			<Card
				sx={{
					bgcolor: "background.level2",
				}}>
				<CardContent>
					<Stack spacing={2}>
						<Button
							fullWidth
							variant="contained"
							type="submit"
							disabled={
								!form.isValid ||
								!form.status.isCardValid ||
								form.isSubmitting ||
								pricing.loading ||
								!!pricingError
							}>
							Pay {pricing.price?.currency?.symbol ?? "$"}{" "}
							{pricing.price?.now?.total ?? "0.00"} and subscribe
						</Button>
						<Stack
							padding={2}
							spacing={2}>
							<Stack
								direction="row"
								spacing={2}
								sx={{ alignItems: "center" }}>
								<Box
									sx={{
										alignItems: "center",
										color: "var(--mui-palette-success-main)",
										display: "flex",
										justifyContent: "center",
									}}>
									<Lock />
								</Box>
								<Typography variant="subtitle2">Secure checkout</Typography>
							</Stack>
							<Stack spacing={2}>
								<Typography
									variant="body2"
									color={"text.secondary"}>
									Your purchases are secured and protected by best encryption
									service - Recurly
								</Typography>
								<Typography
									variant="body2"
									color={"text.secondary"}>
									By providing your card information, you allow Mangomap to
									charge your card for future payments in accordance with the
									Terms of Service.
								</Typography>
								<Typography
									variant="body2"
									color={"text.secondary"}>
									By subscribing you agree to our{" "}
									<Link href={paths.terms}>Terms of Service</Link> and{" "}
									<Link href={paths.privacy}>Privacy Policy</Link>.
								</Typography>
							</Stack>
						</Stack>
					</Stack>
				</CardContent>
			</Card>
		</Stack>
	);
};

// const CouponInput = () => {
// 	const inputRef = useRef<HTMLInputElement>();
// 	const [coupon, setCoupon] = useState<string | null>(null);

// 	return coupon ? (
// 		<Typography
// 			color="text.secondary"
// 			variant="caption">
// 			{coupon}
// 		</Typography>
// 	) : (
// 		<TextField
// 			inputRef={inputRef}
// 			name="coupon"
// 			variant="outlined"
// 			InputProps={{
// 				endAdornment: (
// 					<InputAdornment position="end">
// 						<Button
// 							color="success"
// 							onClick={() => setCoupon(inputRef.current?.value ?? null)}>
// 							Apply
// 						</Button>
// 					</InputAdornment>
// 				),
// 			}}
// 		/>
// 	);
// };
